import { useState, useEffect } from 'react'
import {
  getMailCampaignStats,
  deleteMailCampaign,
  type MailCampaignRead,
  type MailCampaignStats,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { usePermission } from '../../hooks/usePermission'
import { ServiceNoticeMark } from './CampaignKindPicker'
import { EmailPreview } from './EmailPreview'

interface CampaignDetailModalProps {
  campaign: MailCampaignRead
  onClose: () => void
  onUpdate: () => void
  onEdit: () => void
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  scheduled: 'bg-blue-100 text-blue-800',
  sending: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  failed: 'bg-red-100 text-red-800',
}

export function CampaignDetailModal({ campaign, onClose, onUpdate, onEdit }: CampaignDetailModalProps) {
  const [stats, setStats] = useState<MailCampaignStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const [statsError, setStatsError] = useState<string | null>(null)

  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canEdit = usePermission('email_campaigns', 'edit')
  const canDelete = usePermission('email_campaigns', 'delete')

  const isDraft = campaign.status === 'draft'
  const isScheduled = campaign.status === 'scheduled'
  const isSentOrSending = campaign.status === 'sent' || campaign.status === 'sending'

  // Load stats for campaigns that have been sent or are currently sending
  useEffect(() => {
    if (!isSentOrSending) return
    setStatsLoading(true)
    setStatsError(null)
    getMailCampaignStats(campaign.id)
      .then((res) => {
        if (res.ok && res.data) {
          setStats(res.data)
        } else {
          setStatsError('Failed to load stats')
        }
      })
      .catch(() => setStatsError('Failed to load stats'))
      .finally(() => setStatsLoading(false))
  }, [campaign.id, isSentOrSending])

  const handleDelete = async () => {
    setDeleting(true)
    try {
      const res = await deleteMailCampaign(campaign.id)
      if (res.ok) {
        onUpdate()
        onClose()
      } else {
        setError(res.error?.message || 'Delete failed')
      }
    } catch {
      setError('Delete failed')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="font-heading text-xl font-bold text-gray-900 dark:text-gray-100">
              {campaign.name}
            </h2>
            {campaign.kind === 'service_notice' && <ServiceNoticeMark />}
            <span className={`inline-block mt-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[campaign.status] ?? ''}`}>
              {campaign.status}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Campaign info */}
        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Subject:</span>
              <p className="text-gray-900 dark:text-gray-100 font-medium">{campaign.subject}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">From:</span>
              <p className="text-gray-900 dark:text-gray-100 font-medium">{campaign.from_email}</p>
            </div>
            <div className="col-span-2">
              <span className="text-gray-500 dark:text-gray-400">Audience:</span>
              <p className="text-gray-900 dark:text-gray-100 font-medium">{campaign.audience_summary}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Recipients:</span>
              <p className="text-gray-900 dark:text-gray-100 font-medium">
                {campaign.recipient_count.toLocaleString()}
              </p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Template ID:</span>
              <p className="text-gray-900 dark:text-gray-100 font-medium">{campaign.template_id}</p>
            </div>
          </div>

          {/* Listmonk deep link */}
          <div className="flex items-center gap-2">
            <a
              href={campaign.listmonk_admin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-[#503864] dark:text-[#9f7aba] hover:underline font-medium"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Open in Listmonk (full analytics)
            </a>
          </div>

          <div>
            <h4 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Message</h4>
            <EmailPreview
              campaignId={campaign.id}
              subject={campaign.subject}
              fromEmail={campaign.from_email}
              compact
            />
          </div>

          {/* Stats — only for sent/sending campaigns */}
          {isSentOrSending && (
            <div className="rounded-xl bg-gray-50 dark:bg-gray-800 p-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Delivery Stats
              </h4>
              {statsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#503864] border-t-transparent" />
                </div>
              ) : statsError ? (
                <p className="text-sm text-red-600 dark:text-red-400">{statsError}</p>
              ) : stats ? (
                <>
                  <div className="grid grid-cols-4 gap-4 text-center mb-3">
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {stats.delivered.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">Delivered</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">
                        {stats.opened.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">Opened</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-600">
                        {stats.clicked.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">Clicked</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">
                        {stats.bounced.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">Bounced</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                        {stats.unsubscribed.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">Unsubscribed</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                        {stats.spam_complaints.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">Spam Reports</p>
                    </div>
                  </div>
                  {stats.delivered > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 text-center">
                      Open rate: {((stats.opened / stats.delivered) * 100).toFixed(1)}% |{' '}
                      Click rate: {((stats.clicked / stats.delivered) * 100).toFixed(1)}%
                      {' '}·{' '}
                      Last updated: {new Date(stats.last_updated_at).toLocaleString()}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}

          {/* Timestamps */}
          <div className="text-xs text-gray-500 space-y-1">
            {campaign.scheduled_at && (
              <p>Scheduled for: {new Date(campaign.scheduled_at).toLocaleString()}</p>
            )}
            {campaign.started_at && (
              <p>Started at: {new Date(campaign.started_at).toLocaleString()}</p>
            )}
            {campaign.completed_at && (
              <p>Completed at: {new Date(campaign.completed_at).toLocaleString()}</p>
            )}
          </div>
        </div>

        {/* Delete confirmation */}
        {confirmDelete && (
          <div className="mb-6 rounded-xl border border-red-200 dark:border-red-800 p-4 bg-red-50 dark:bg-red-900/20">
            <h4 className="font-medium text-red-900 dark:text-red-100 mb-3">Delete Campaign</h4>
            <p className="text-sm text-red-700 dark:text-red-300 mb-4">
              This cannot be undone. The campaign will be removed from Listmonk.
            </p>
            <div className="flex gap-2">
              <CustomButton onClick={handleDelete} disabled={deleting} variant="danger">
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </CustomButton>
              <CustomButton onClick={() => setConfirmDelete(false)} variant="secondary">
                Cancel
              </CustomButton>
            </div>
          </div>
        )}

        {/* Action buttons */}
        {!confirmDelete && (
          <div className="flex flex-wrap gap-2">
            {(isDraft || isScheduled) && canEdit && (
              <CustomButton onClick={onEdit} variant="secondary">
                Edit
              </CustomButton>
            )}
            {(isDraft || isScheduled) && canDelete && (
              <CustomButton onClick={() => setConfirmDelete(true)} variant="danger">
                Delete
              </CustomButton>
            )}
            {isSentOrSending && (
              <a
                href={campaign.listmonk_admin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-xl bg-gray-100 dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                View in Listmonk
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
